import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { AddFilterAction, SelectInput } from '~/components';
import { ApprovalCaseFilters } from '~/api/approvals';
import { ApprovalCaseStatus } from '~/types/approvalCase';
import { Moment } from '~/types';
import { time } from '~/utils';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import {
  ActionTypeFilterInput,
  ObjectTypeFilterInput,
  ObjectIdFilterInput,
  RequestedByIdFilterInput,
} from './ApprovalFilterInputs';

const resource = 'approvals/history';

interface Props {
  cleanFilters?: boolean;
}

const HistoryListFilters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.approvals.list.filters.${key}`);
  const filters = useSelector(selectors.getFilters<ApprovalCaseFilters>(resource));
  const [localValues, setLocalValues] = useState<ApprovalCaseFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const activeKeys = Object.keys(filters).filter((k) => !!(filters as any)[k]);
    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues((prev) => ({ ...prev, [inputType]: data }));
    dispatchFilterChange(inputType, data);
  };

  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({ ...localValues, [name]: value });
    debouncedDispatchFilterChange(name, value);
  };

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            completedFrom: time().subtract(1, 'months'),
            completedTo: time(),
          },
        })
      );
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  const hideFilter = (filter: string) => {
    dispatchFilterChange(filter, '');
    if (filter === 'objectType') {
      dispatchFilterChange('objectId', '');
      setAddedFilters(addedFilters.filter((f) => f !== filter && f !== 'objectId'));
    } else {
      setAddedFilters(addedFilters.filter((f) => f !== filter));
    }
  };

  const handleObjectTypeChange = (e: any) => {
    const { value } = e.target;
    setLocalValues({ ...localValues, objectType: value, objectId: undefined });
    dispatchFilterChange('objectType', value);
    dispatchFilterChange('objectId', '');
  };

  const allFilters = [
    'status',
    'actionType',
    'objectType',
    'objectId',
    'requestedById',
    'submittedFrom',
    'submittedTo',
  ];

  const statusOptions: { value: ApprovalCaseStatus; text: string }[] = [
    { value: 'APPROVED', text: t('statusOptions.APPROVED') },
    { value: 'REJECTED', text: t('statusOptions.REJECTED') },
    { value: 'CANCELLED', text: t('statusOptions.CANCELLED') },
    { value: 'FAILED', text: t('statusOptions.FAILED') },
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'status':
        return (
          <SelectInput<ApprovalCaseStatus | undefined>
            label={t('status')}
            name='status'
            value={localValues.status as ApprovalCaseStatus | undefined}
            options={statusOptions}
            onChange={(e: any) => {
              const { value } = e.target;
              setLocalValues({ ...localValues, status: value });
              dispatchFilterChange('status', value);
            }}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'actionType':
        return (
          <ActionTypeFilterInput
            value={localValues.actionType}
            onChange={(e: any) => {
              const { value } = e.target;
              setLocalValues({ ...localValues, actionType: value });
              dispatchFilterChange('actionType', value);
            }}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'objectType':
        return (
          <ObjectTypeFilterInput
            value={localValues.objectType}
            onChange={handleObjectTypeChange}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'objectId':
        return (
          <ObjectIdFilterInput
            value={localValues.objectId}
            onChange={debouncedHandleInput}
            disabled={!localValues.objectType}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'requestedById':
        return (
          <RequestedByIdFilterInput
            value={localValues.requestedById}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'submittedFrom':
        return (
          <DateTimePicker
            className={cx(
              listFilterClasses.formControl,
              listFilterClasses.formControlLong,
              listFilterClasses.datePickers,
              listFilterClasses.formControlAdd
            )}
            format='L - LT'
            ampm={hasCurrentLocale12HourFormat()}
            margin='normal'
            label={t('submittedFrom')}
            value={localValues.submittedFrom ?? null}
            onChange={(date) => handleDateTimeChange(date as Moment, 'submittedFrom')}
          />
        );
      case 'submittedTo':
        return (
          <DateTimePicker
            className={cx(
              listFilterClasses.formControl,
              listFilterClasses.formControlLong,
              listFilterClasses.datePickers,
              listFilterClasses.formControlAdd
            )}
            format='L - LT'
            ampm={hasCurrentLocale12HourFormat()}
            margin='normal'
            label={t('submittedTo')}
            value={localValues.submittedTo ?? null}
            onChange={(date) => handleDateTimeChange(date as Moment, 'submittedTo')}
          />
        );
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (!component) return null;
      return (
        <div className={listFilterClasses.flexContainer} key={filter}>
          {component}
          <IconButton
            aria-label='delete'
            className={listFilterClasses.deleteIcon}
            onClick={() => hideFilter(filter)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      );
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('completedFrom')}
                value={localValues.completedFrom ?? null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'completedFrom')}
                maxDate={localValues.completedTo || time().format()}
              />
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('completedTo')}
                value={localValues.completedTo ?? null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'completedTo')}
                maxDate={time().format()}
                minDate={localValues.completedFrom || undefined}
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({ key: filterKey, label: t(filterKey) }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const useStyles = makeStyles(() => ({
  leftSide: {
    maxWidth: 'calc(100% - 100px)',
  },
  rightSide: { minWidth: 100, textAlign: 'right' },
}));

export default HistoryListFilters;
